import { imagineRoutingStore } from "@/web-components";
import { type GetProjectResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { useQuery } from "@tanstack/react-query";
import { imagineStore } from "@/stores/imagine.store";
import { toast } from "sonner";
import { ExclamationCircleIcon } from "@heroicons/react/16/solid";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";

export function useFetchProject() {
  const isReadonly = imagineStore.use.isReadonly();
  const project = imagineStore.use.project();
  const setProject = imagineStore.use.setProject();
  const navigate = imagineRoutingStore.use.navigate();
  const isGenerating = imagineStore.use.chat.isGenerating();
  const isGeneratingNewProject = imagineStore.use.isGeneratingNewProject();
  const projectId = imagineStore.use.projectId();
  const enabled = Boolean(
    !isGenerating && !isGeneratingNewProject && !!projectId && !project,
  );

  const fetchProjectQuery = useQuery<GetProjectResponseBody | undefined>({
    queryKey: ["project", projectId],
    enabled: enabled,
    queryFn: async () => {
      const url = !isReadonly
        ? `/api/projects/${projectId}`
        : `/api/featured-projects/${projectId}`;

      const response = await aiServiceFetch(url, {
        method: "GET",
      });

      if (response.status === 404) {
        navigate({ id: "home", props: {} });
        toast.error("Project not found", {
          icon: <ExclamationCircleIcon className="size-4 text-red-500" />,
        });
        return;
      }

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      const data = (await response.json()) as GetProjectResponseBody;
      setProject(data);
      return data;
    },
  });

  return fetchProjectQuery;
}
